import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Power, 
  Save, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2,
  XCircle,
  Calendar,
  CreditCard,
  UserPlus,
  X,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminServiceAvailability = () => {
  const { getAuthHeader, token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    fitnetEnabled: true,
    updatedAt: null,
    updatedBy: null
  });

  useEffect(() => {
    if (token) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/service-availability`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Błąd pobierania ustawień');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/service-availability`, 
        { fitnetEnabled: settings.fitnetEnabled },
        { headers: getAuthHeader() }
      );
      toast.success(settings.fitnetEnabled 
        ? 'Operacje online WŁĄCZONE' 
        : 'Operacje online WYŁĄCZONE'
      );
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Błąd zapisu ustawień');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pl-PL');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Power className="w-7 h-7 text-primary" />
          Dostępność usług online
        </h1>
        <p className="text-muted-foreground mt-1">
          Zarządzaj dostępnością rezerwacji, zakupu karnetów i rejestracji
        </p>
      </div>

      {/* Main Toggle Card */}
      <Card className={`border-2 transition-colors ${
        settings.fitnetEnabled 
          ? 'bg-green-500/5 border-green-500/30' 
          : 'bg-red-500/10 border-red-500/30'
      }`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings.fitnetEnabled ? (
                <CheckCircle2 className="w-6 h-6 text-green-500" />
              ) : (
                <XCircle className="w-6 h-6 text-red-500" />
              )}
              <span className="text-white">
                Operacje online (FitNet)
              </span>
            </div>
            <Switch
              checked={settings.fitnetEnabled}
              onCheckedChange={(checked) => setSettings({ ...settings, fitnetEnabled: checked })}
              className="data-[state=checked]:bg-green-500"
            />
          </CardTitle>
          <CardDescription className="text-base">
            {settings.fitnetEnabled ? (
              <span className="text-green-400">
                ✓ System działa normalnie - wszystkie operacje są dostępne
              </span>
            ) : (
              <span className="text-red-400">
                ✗ Operacje online są WYŁĄCZONE - klienci nie mogą rezerwować ani kupować
              </span>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* What gets disabled */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Co zostaje {settings.fitnetEnabled ? 'włączone' : 'wyłączone'}?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              settings.fitnetEnabled 
                ? 'bg-green-500/10 border-green-500/20' 
                : 'bg-red-500/10 border-red-500/20'
            }`}>
              <Calendar className={`w-5 h-5 ${settings.fitnetEnabled ? 'text-green-500' : 'text-red-500'}`} />
              <div>
                <p className="font-medium text-white">Rezerwacje zajęć</p>
                <p className="text-sm text-muted-foreground">
                  Tworzenie i anulowanie rezerwacji
                </p>
              </div>
            </div>
            
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              settings.fitnetEnabled 
                ? 'bg-green-500/10 border-green-500/20' 
                : 'bg-red-500/10 border-red-500/20'
            }`}>
              <CreditCard className={`w-5 h-5 ${settings.fitnetEnabled ? 'text-green-500' : 'text-red-500'}`} />
              <div>
                <p className="font-medium text-white">Zakup karnetów</p>
                <p className="text-sm text-muted-foreground">
                  Kupowanie i aktywacja karnetów
                </p>
              </div>
            </div>
            
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              settings.fitnetEnabled 
                ? 'bg-green-500/10 border-green-500/20' 
                : 'bg-red-500/10 border-red-500/20'
            }`}>
              <UserPlus className={`w-5 h-5 ${settings.fitnetEnabled ? 'text-green-500' : 'text-red-500'}`} />
              <div>
                <p className="font-medium text-white">Rejestracja kont</p>
                <p className="text-sm text-muted-foreground">
                  Tworzenie nowych kont użytkowników
                </p>
              </div>
            </div>
            
            <div className={`flex items-center gap-3 p-4 rounded-lg border ${
              settings.fitnetEnabled 
                ? 'bg-green-500/10 border-green-500/20' 
                : 'bg-red-500/10 border-red-500/20'
            }`}>
              <X className={`w-5 h-5 ${settings.fitnetEnabled ? 'text-green-500' : 'text-red-500'}`} />
              <div>
                <p className="font-medium text-white">Anulowanie rezerwacji</p>
                <p className="text-sm text-muted-foreground">
                  Odwoływanie istniejących rezerwacji
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info about translations */}
      <Card className="bg-blue-500/10 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Komunikaty dla użytkowników</p>
              <p className="text-sm text-muted-foreground mt-1">
                Teksty wyświetlane użytkownikom gdy system jest wyłączony możesz edytować w zakładce{' '}
                <a href="/admin/translations" className="text-primary hover:underline">
                  Tłumaczenia → Maintenance
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Last update info */}
      {settings.updatedAt && (
        <div className="text-sm text-muted-foreground">
          Ostatnia zmiana: {formatDate(settings.updatedAt)} przez {settings.updatedBy || 'admin'}
        </div>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          size="lg"
          className={settings.fitnetEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Zapisywanie...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {settings.fitnetEnabled ? 'Zapisz (WŁĄCZONE)' : 'Zapisz (WYŁĄCZONE)'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AdminServiceAvailability;
